/**
 * EventReportViewer Component
 *
 * Main viewer for HMC event reports with filtering, search, and export functionality.
 * View mode is responsive and automatic:
 * - Mobile (< 768px): Card/grid view only
 * - Desktop (>= 768px): Table view only
 *
 * Supports:
 * - Double-click (desktop) and long-press (mobile) to open event detail modal
 * - Responsive view mode (card on mobile, table on desktop - no toggle)
 * - Event selection and enrichment
 */

import React, { useMemo, useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { EventReportCard } from './EventReportCard';
import { EventDetailModal } from './EventDetailModal';
import { BackupSelectionFloatingButton } from './BackupSelectionFloatingButton';
import { EditableTableCell, type SelectOption } from './EditableTableCell';
import { TicketAssignmentModal } from './TicketAssignmentModal';
import type { HMCEvent, UploadHistory, MusicGenre, EventTypeExtended } from '../hmcEventsStore';
import { useHMCEventsStore } from '../hmcEventsStore';
import { parseAttendance, getTotalTickets, getTicketsRemaining, getTicketCapacitySplit, getCleanEventName, isAssignedSuiteEvent } from '../utils/attendanceParser';
import { detectEventType } from '../utils/eventTypeDetector';
import { parseLocalDate } from '../utils/dateUtils';
import { useIsMobile } from '@shared/hooks/useIsMobile';
import { useGuestView } from '../GuestViewContext';

// Event type options for dropdown
const EVENT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'dynamo', label: 'Dynamo', color: 'orange' },
  { value: 'dash', label: 'Dash', color: 'sky' },
  { value: 'rockets', label: 'Rockets', color: 'red' },
  { value: 'concert', label: 'Concert', color: 'purple' },
  { value: 'comedy', label: 'Comedy', color: 'yellow' },
  { value: 'performance', label: 'Performance', color: 'pink' },
  { value: 'general', label: 'General', color: 'gray' },
  { value: 'other', label: 'Other', color: 'slate' },
];

// Genre options for dropdown
const GENRE_OPTIONS: SelectOption[] = [
  { value: 'rap', label: 'Hip-Hop' },
  { value: 'rnb', label: 'R&B' },
  { value: 'latino', label: 'Latino' },
  { value: 'rock', label: 'Rock' },
  { value: 'pop', label: 'Pop' },
  { value: 'country', label: 'Country' },
  { value: 'electronic', label: 'EDM' },
  { value: 'other', label: 'Other' },
];

// Genre display configuration (matches EventReportCard)
// Using white text with darker backgrounds for WCAG contrast compliance
const GENRE_STYLES: Record<MusicGenre, { bg: string; text: string; label: string }> = {
  rap: { bg: 'bg-amber-600/80', text: 'text-white', label: 'Hip-Hop' },
  rnb: { bg: 'bg-purple-600/80', text: 'text-white', label: 'R&B' },
  latino: { bg: 'bg-red-600/80', text: 'text-white', label: 'Latino' },
  rock: { bg: 'bg-slate-600/80', text: 'text-white', label: 'Rock' },
  pop: { bg: 'bg-pink-600/80', text: 'text-white', label: 'Pop' },
  country: { bg: 'bg-yellow-600/80', text: 'text-white', label: 'Country' },
  electronic: { bg: 'bg-cyan-600/80', text: 'text-white', label: 'EDM' },
  other: { bg: 'bg-dark-surface/50/80', text: 'text-white', label: 'Other' },
};

// Sort options
type SortOption = 'date' | 'tickets' | 'name';
type SortDirection = 'asc' | 'desc';

// Filter options for availability
type AvailabilityFilter = 'all' | 'has_tickets' | 'sold_out' | 'no_bookings';

// Filter options for enrichment status
type EnrichmentFilter = 'all' | 'enriched' | 'not_enriched' | 'pending' | 'failed';

// Event temporal tab: current (upcoming) or past events
type EventTemporalTab = 'current' | 'past';

// Table view icon
const TableIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

// Icons
const GridIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

const ListIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const RefreshIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const SortAscIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
  </svg>
);

const SortDescIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
  </svg>
);

const TicketIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
  </svg>
);

interface EventReportViewerProps {
  events: HMCEvent[];
  reportInfo: UploadHistory;
  loading?: boolean;
}

export const EventReportViewer: React.FC<EventReportViewerProps> = ({
  events,
  reportInfo,
  loading = false,
}) => {
  const {
    searchQuery,
    setSearchQuery,
    eventTypeFilter,
    setEventTypeFilter,
    enrichSelectedEvents,
    selectedEventIds,
    toggleEventSelection,
    selectAllEvents,
    deselectAllEvents,
    selectFilteredEvents,
    backupSelection,
    restoreBackupSelection,
    updateEvent,
  } = useHMCEventsStore();

  // Responsive view mode: card/grid on mobile, table on desktop
  // No user toggle - automatically determined by screen size
  const isMobile = useIsMobile();
  const effectiveViewMode = isMobile ? 'grid' : 'table';

  // Guest view mode
  const { isGuestView, maxGuestTickets } = useGuestView();

  // Modal state for event details
  const [selectedEvent, setSelectedEvent] = useState<HMCEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Assignment modal state
  const [assignmentEvent, setAssignmentEvent] = useState<HMCEvent | null>(null);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);

  // Panel state for docked mode (affects main content width)
  const [isPanelDocked, setIsPanelDocked] = useState(false);
  const [dockedPanelWidth, setDockedPanelWidth] = useState(0);

  // Handle panel state changes from EventDetailModal
  const handlePanelStateChange = useCallback((isDocked: boolean, panelWidth: number) => {
    setIsPanelDocked(isDocked);
    setDockedPanelWidth(panelWidth);
  }, []);

  // Re-enrichment confirmation modal state
  const [showReEnrichConfirm, setShowReEnrichConfirm] = useState(false);
  const [alreadyEnrichedEvents, setAlreadyEnrichedEvents] = useState<HMCEvent[]>([]);

  // Sort and additional filter state
  const [sortBy, setSortBy] = useState<SortOption>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [availabilityFilter, setAvailabilityFilter] = useState<AvailabilityFilter>('all');
  const [enrichmentFilter, setEnrichmentFilter] = useState<EnrichmentFilter>('all');

  // Current/Past events tab state
  const [eventTemporalTab, setEventTemporalTab] = useState<EventTemporalTab>('current');

  // Handle opening the detail modal
  const handleViewDetails = useCallback((event: HMCEvent) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  }, []);

  // Handle closing the modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  }, []);

  // Handle opening the assignment modal
  const handleOpenAssignmentModal = useCallback((event: HMCEvent) => {
    setAssignmentEvent(event);
    setIsAssignmentModalOpen(true);
  }, []);

  // Handle closing the assignment modal
  const handleCloseAssignmentModal = useCallback(() => {
    setIsAssignmentModalOpen(false);
    setAssignmentEvent(null);
  }, []);

  // Get today's date at midnight for comparing event dates
  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  // Helper to check if an event date has passed
  const isEventPast = (eventDateStr: string | null): boolean => {
    if (!eventDateStr) return false;
    const eventDate = parseLocalDate(eventDateStr);
    if (!eventDate) return false;
    return eventDate < today;
  };

  // Filter and search events
  const filteredEvents = useMemo(() => {
    let result = [...events];

    // Apply temporal filter (current vs past events)
    result = result.filter((e) => {
      const isPast = isEventPast(e.event_date);
      return eventTemporalTab === 'past' ? isPast : !isPast;
    });

    // Apply event type filter
    if (eventTypeFilter !== 'all') {
      if (eventTypeFilter === 'concert') {
        // Concerts: events with music_genre set OR event_type is 'concert'
        result = result.filter((e) => e.music_genre || e.event_type === 'concert');
      } else if (eventTypeFilter === 'non_sporting') {
        // Non-Sporting: concerts, comedy, performances, other - anything NOT dynamo/dash/rockets
        result = result.filter((e) => {
          // Use text-based detection to exclude sporting events
          const eventText = `${e.opponent || ''} ${e.event_description || ''} ${e.venue || ''} ${e.special_event || ''}`.toLowerCase();
          const isSporting = e.event_type === 'dynamo' || eventText.includes('dynamo') ||
                            e.event_type === 'dash' || eventText.includes('dash') ||
                            e.event_type === 'rockets' || eventText.includes('rockets') || eventText.includes('houston rockets');
          if (isSporting) return false;
          // Include if has music genre (concert)
          if (e.music_genre) return true;
          // Include comedy, performance, other types
          const isComedy = eventText.includes('comedy') || eventText.includes('comedian') || eventText.includes('stand-up') || eventText.includes('standup');
          const isPerformance = eventText.includes('theater') || eventText.includes('theatre') || eventText.includes('ballet') || eventText.includes('broadway') || eventText.includes('dance') || eventText.includes('musical') || eventText.includes('opera') || eventText.includes('cirque');
          return isComedy || isPerformance || e.event_type === 'general' || e.event_type === 'concert' || e.event_type === 'comedy' || e.event_type === 'performance' || e.event_type === 'other';
        });
      } else if (eventTypeFilter === 'dynamo' || eventTypeFilter === 'dash' || eventTypeFilter === 'rockets') {
        // Sports teams: use text-based detection
        result = result.filter((e) => {
          const eventText = `${e.opponent || ''} ${e.event_description || ''} ${e.venue || ''} ${e.special_event || ''}`.toLowerCase();
          if (eventTypeFilter === 'dynamo') {
            return e.event_type === 'dynamo' || eventText.includes('dynamo');
          } else if (eventTypeFilter === 'dash') {
            return e.event_type === 'dash' || eventText.includes('dash');
          } else {
            return e.event_type === 'rockets' || eventText.includes('rockets') || eventText.includes('houston rockets');
          }
        });
      } else {
        // Direct event_type match (general, concert, comedy, performance, other)
        result = result.filter((e) => e.event_type === eventTypeFilter);
      }
    }

    // Apply availability filter (based on EB tickets, not total)
    if (availabilityFilter !== 'all') {
      result = result.filter((e) => {
        const capacitySplit = getTicketCapacitySplit(e.special_event, e.opponent, e.event_type, e.venue);
        if (availabilityFilter === 'has_tickets') {
          // Show only events with EB tickets still available
          return capacitySplit.ebRemaining > 0;
        } else if (availabilityFilter === 'sold_out') {
          // Show only events where EB is sold out
          return capacitySplit.ebRemaining === 0 && capacitySplit.ebUsed > 0;
        } else if (availabilityFilter === 'no_bookings') {
          // Show only events with no bookings yet (full capacity available)
          return capacitySplit.pcUsed === 0 && capacitySplit.ebUsed === 0;
        }
        return true;
      });
    }

    // Apply enrichment filter
    if (enrichmentFilter !== 'all') {
      result = result.filter((e) => {
        const status = e.enrichment_status || 'pending';
        if (enrichmentFilter === 'enriched') {
          return status === 'enriched';
        } else if (enrichmentFilter === 'not_enriched') {
          return status !== 'enriched';
        } else if (enrichmentFilter === 'pending') {
          return status === 'pending' || !e.enrichment_status;
        } else if (enrichmentFilter === 'failed') {
          return status === 'failed';
        }
        return true;
      });
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          e.opponent?.toLowerCase().includes(query) ||
          e.venue?.toLowerCase().includes(query) ||
          e.special_event?.toLowerCase().includes(query) ||
          e.broadcast?.toLowerCase().includes(query)
      );
    }

    // Sort events
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'date':
          comparison = new Date(a.event_date).getTime() - new Date(b.event_date).getTime();
          break;
        case 'tickets':
          comparison = getTotalTickets(a.special_event) - getTotalTickets(b.special_event);
          break;
        case 'name':
          comparison = (a.opponent || '').localeCompare(b.opponent || '');
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [events, eventTypeFilter, availabilityFilter, enrichmentFilter, searchQuery, sortBy, sortDirection, eventTemporalTab, today]);

  // Helper to detect sports team type from text content (for stats)
  const detectSportsTeam = (event: HMCEvent) => {
    const eventText = `${event.opponent || ''} ${event.event_description || ''} ${event.venue || ''} ${event.special_event || ''}`.toLowerCase();
    return {
      isDynamo: event.event_type === 'dynamo' || eventText.includes('dynamo'),
      isDash: event.event_type === 'dash' || eventText.includes('dash'),
      isRockets: event.event_type === 'rockets' || eventText.includes('rockets') || eventText.includes('houston rockets'),
    };
  };

  // Stats - use text-based detection for accurate counts
  const stats = useMemo(() => {
    const dynamo = events.filter((e) => detectSportsTeam(e).isDynamo).length;
    const dash = events.filter((e) => detectSportsTeam(e).isDash).length;
    const rockets = events.filter((e) => detectSportsTeam(e).isRockets).length;
    const enriched = events.filter((e) => e.enrichment_status === 'enriched').length;
    const failed = events.filter((e) => e.enrichment_status === 'failed').length;
    const selected = selectedEventIds.size;
    const totalTickets = events.reduce((sum, e) => sum + getTotalTickets(e.special_event), 0);
    const eventsWithTickets = events.filter((e) => getTotalTickets(e.special_event) > 0).length;

    return { total: events.length, dynamo, dash, rockets, enriched, failed, selected, totalTickets, eventsWithTickets };
  }, [events, selectedEventIds]);

  // Check if all filtered events are selected
  const allFilteredSelected = useMemo(() => {
    return filteredEvents.length > 0 && filteredEvents.every((e) => selectedEventIds.has(e.id));
  }, [filteredEvents, selectedEventIds]);

  // Handle select all for filtered events (excludes assigned suite events)
  const handleSelectAll = () => {
    if (allFilteredSelected) {
      deselectAllEvents();
    } else {
      // Exclude assigned suite events from Select All
      const selectableEvents = filteredEvents.filter(
        (e) => !isAssignedSuiteEvent(e.opponent)
      );
      selectFilteredEvents(selectableEvents.map((e) => e.id));
    }
  };

  // Handle export - Generate .xlsx with event data
  const handleExport = () => {
    // Use filtered events (respects current/past filter and other filters)
    const exportData = filteredEvents.map((event) => {
      const eventDate = parseLocalDate(event.event_date);
      const typeResult = detectEventType(event);
      const capacitySplit = getTicketCapacitySplit(event.special_event, event.opponent, event.event_type, event.venue);

      return {
        'Date': eventDate ? eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD',
        'Start Time': event.event_start_time || event.kickoff_time || 'TBD',
        'Event Name': getCleanEventName(event.opponent) || 'TBD',
        'Type': typeResult.label || 'Unknown',
        '# Tickets Available': capacitySplit.ebRemaining,
      };
    });

    // Create worksheet and workbook
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Events');

    // Generate filename with date
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD
    const tabLabel = eventTemporalTab === 'past' ? 'Past' : 'Current';
    const filename = `HMC_Events_${tabLabel}_${dateStr}.xlsx`;

    // Download the file
    XLSX.writeFile(wb, filename);
  };

  // Handle sort change
  const handleSortChange = (newSortBy: SortOption) => {
    if (sortBy === newSortBy) {
      // Toggle direction if same sort
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(newSortBy);
      setSortDirection('asc');
    }
  };

  // Handle enrichment of selected events - check for already enriched events first
  const handleEnrich = async () => {
    // Find already enriched events in selection
    const enrichedInSelection = events.filter(
      (e) => selectedEventIds.has(e.id) && e.enrichment_status === 'enriched'
    );

    if (enrichedInSelection.length > 0) {
      // Show confirmation modal for already enriched events
      setAlreadyEnrichedEvents(enrichedInSelection);
      setShowReEnrichConfirm(true);
    } else {
      // No already enriched events, proceed directly
      await enrichSelectedEvents(reportInfo.upload_id);
    }
  };

  // Confirm re-enrichment and proceed
  const handleConfirmReEnrich = async () => {
    setShowReEnrichConfirm(false);
    setAlreadyEnrichedEvents([]);
    await enrichSelectedEvents(reportInfo.upload_id);
  };

  // Cancel re-enrichment
  const handleCancelReEnrich = () => {
    setShowReEnrichConfirm(false);
    setAlreadyEnrichedEvents([]);
  };

  // Get current event index in filtered list for modal navigation
  const currentEventIndex = useMemo(() => {
    if (!selectedEvent) return -1;
    return filteredEvents.findIndex((e) => e.id === selectedEvent.id);
  }, [selectedEvent, filteredEvents]);

  // Navigate to previous event in modal
  const handlePreviousEvent = useCallback(() => {
    if (currentEventIndex > 0) {
      setSelectedEvent(filteredEvents[currentEventIndex - 1]);
    }
  }, [currentEventIndex, filteredEvents]);

  // Navigate to next event in modal
  const handleNextEvent = useCallback(() => {
    if (currentEventIndex < filteredEvents.length - 1) {
      setSelectedEvent(filteredEvents[currentEventIndex + 1]);
    }
  }, [currentEventIndex, filteredEvents]);

  return (
    <div
      className="space-y-6 transition-[margin] duration-200 ease-in-out"
      style={{ marginRight: isPanelDocked ? `${dockedPanelWidth}px` : 0 }}
      data-testid="event-report-viewer"
    >
      {/* Current/Past Events Tabs - shown in both admin and guest view */}
      <div className="flex items-center gap-1 mb-4">
        <button
          onClick={() => setEventTemporalTab('current')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
            eventTemporalTab === 'current'
              ? 'bg-brand-500 text-white'
              : 'bg-dark-surface border border-dark-border/30 text-dark-muted hover:text-white hover:bg-dark-border/50'
          }`}
          data-testid="current-events-tab"
        >
          Current Events
        </button>
        <button
          onClick={() => setEventTemporalTab('past')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
            eventTemporalTab === 'past'
              ? 'bg-amber-600 text-white'
              : 'bg-dark-surface border border-dark-border/30 text-dark-muted hover:text-white hover:bg-dark-border/50'
          }`}
          data-testid="past-events-tab"
        >
          Past Events
        </button>
        {/* Show count of past events */}
        {eventTemporalTab === 'current' && (
          <span className="ml-2 text-xs text-dark-muted">
            ({events.filter(e => isEventPast(e.event_date)).length} past events hidden)
          </span>
        )}
      </div>

      {/* Filters, search, and stats - hidden in guest view - STICKY below page header */}
      {!isGuestView && (
        <div className="space-y-3 sticky top-[72px] md:top-[88px] z-10 bg-dark-bg pt-3 pb-2 -mt-3 -mx-4 px-4 md:-mx-6 md:px-6">
          {/* Main toolbar row: Select All + Search + Filters */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            {/* Select All checkbox */}
            <label className="flex items-center gap-2 cursor-pointer min-h-[44px] px-3 py-2 bg-dark-surface border border-dark-border/30 rounded-xl shrink-0">
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={handleSelectAll}
                className="w-5 h-5 rounded border-dark-border/30 bg-dark-bg text-brand-500 focus:ring-brand-500 focus:ring-offset-0 cursor-pointer"
                data-testid="event-select-all-checkbox"
              />
              <span className="text-sm text-dark-text whitespace-nowrap">
                {allFilteredSelected ? 'Deselect' : 'Select all'} ({filteredEvents.length})
              </span>
            </label>

            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-dark-surface border border-dark-border/30 rounded-xl text-white placeholder-dark-muted focus:outline-none focus:border-brand-500 min-h-[44px]"
                data-testid="event-search-input"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted">
                <SearchIcon />
              </span>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Event type filter */}
              <select
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value as any)}
                className="px-3 py-2 bg-dark-surface border border-dark-border/30 rounded-xl text-white min-h-[44px] text-sm"
                data-testid="event-type-filter"
              >
                <option value="all">All Types</option>
                <option value="dynamo">Dynamo</option>
                <option value="dash">Dash</option>
                <option value="rockets">Rockets</option>
                <option value="concert">Concerts</option>
                <option value="non_sporting">Non-Sporting</option>
                <option value="general">General</option>
              </select>

              {/* Availability filter */}
              <select
                value={availabilityFilter}
                onChange={(e) => setAvailabilityFilter(e.target.value as AvailabilityFilter)}
                className="px-3 py-2 bg-dark-surface border border-dark-border/30 rounded-xl text-white min-h-[44px] text-sm"
                data-testid="availability-filter"
              >
                <option value="all">All Availability</option>
                <option value="has_tickets">Has Tickets</option>
                <option value="sold_out">Sold Out</option>
                <option value="no_bookings">No Bookings Yet</option>
              </select>

              {/* Enrichment filter */}
              <select
                value={enrichmentFilter}
                onChange={(e) => setEnrichmentFilter(e.target.value as EnrichmentFilter)}
                className="px-3 py-2 bg-dark-surface border border-dark-border/30 rounded-xl text-white min-h-[44px] text-sm"
                data-testid="enrichment-filter"
              >
                <option value="all">All Enrichment</option>
                <option value="enriched">Enriched</option>
                <option value="not_enriched">Not Enriched</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>

              {/* Sort controls - same height as other buttons */}
              <div className="flex items-center bg-dark-surface border border-dark-border/30 rounded-xl min-h-[44px]">
                <button
                  onClick={() => handleSortChange('date')}
                  className={`px-3 py-2 rounded-l-lg text-sm font-medium transition-all duration-150 flex items-center gap-1 h-full ${
                    sortBy === 'date'
                      ? 'bg-brand-500 text-white'
                      : 'text-dark-muted hover:text-white hover:bg-dark-border/50'
                  }`}
                  title="Sort by date"
                  data-testid="sort-by-date"
                >
                  Date
                  {sortBy === 'date' && (sortDirection === 'asc' ? <SortAscIcon /> : <SortDescIcon />)}
                </button>
                <button
                  onClick={() => handleSortChange('tickets')}
                  className={`px-3 py-2 text-sm font-medium transition-all duration-150 flex items-center gap-1 h-full border-x border-dark-border/30 ${
                    sortBy === 'tickets'
                      ? 'bg-brand-500 text-white'
                      : 'text-dark-muted hover:text-white hover:bg-dark-border/50'
                  }`}
                  title="Sort by tickets"
                  data-testid="sort-by-tickets"
                >
                  Tickets
                  {sortBy === 'tickets' && (sortDirection === 'asc' ? <SortAscIcon /> : <SortDescIcon />)}
                </button>
                <button
                  onClick={() => handleSortChange('name')}
                  className={`px-3 py-2 rounded-r-lg text-sm font-medium transition-all duration-150 flex items-center gap-1 h-full ${
                    sortBy === 'name'
                      ? 'bg-brand-500 text-white'
                      : 'text-dark-muted hover:text-white hover:bg-dark-border/50'
                  }`}
                  title="Sort by name"
                  data-testid="sort-by-name"
                >
                  Name
                  {sortBy === 'name' && (sortDirection === 'asc' ? <SortAscIcon /> : <SortDescIcon />)}
                </button>
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-1.5">
                {/* Enrich button */}
                <button
                  onClick={handleEnrich}
                  disabled={stats.selected === 0}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-all duration-150 text-xs font-medium min-h-[36px] ${
                    stats.selected > 0
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-dark-border text-dark-muted cursor-not-allowed'
                  }`}
                  title={stats.selected > 0 ? `Enrich ${stats.selected} events` : 'Select events to enrich'}
                  data-testid="enrich-button"
                >
                  <RefreshIcon />
                  <span className="hidden sm:inline">Enrich</span>
                </button>

                {/* Export button */}
                <button
                  onClick={handleExport}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-all duration-150 text-xs font-medium min-h-[36px]"
                  title="Export to Excel"
                  data-testid="export-button"
                >
                  <DownloadIcon />
                  <span className="hidden sm:inline">Export</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results count - hidden in guest view */}
      {!isGuestView && searchQuery && (
        <p className="text-sm text-dark-muted">
          Showing {filteredEvents.length} of {events.length} events
        </p>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        </div>
      )}

      {/* Events grid/list/table - responsive: card on mobile, table on desktop */}
      {!loading && filteredEvents.length > 0 && effectiveViewMode !== 'table' && (
        <div
          className={
            effectiveViewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
              : 'space-y-4'
          }
        >
          {filteredEvents.map((event) => (
            <EventReportCard
              key={event.id}
              event={event}
              isSelected={selectedEventIds.has(event.id)}
              onToggleSelection={toggleEventSelection}
              onViewDetails={handleViewDetails}
              onEditAssignments={handleOpenAssignmentModal}
              isGuestView={isGuestView}
              maxGuestTickets={maxGuestTickets}
            />
          ))}
        </div>
      )}

      {/* Table view - Modern professional design (desktop only) */}
      {!loading && filteredEvents.length > 0 && effectiveViewMode === 'table' && (
        <div className="hmc-table-container">
          <table className="hmc-modern-table">
            <thead>
              <tr>
                <th className="hmc-th-checkbox">
                  <div className="hmc-checkbox-wrapper">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={handleSelectAll}
                      className="hmc-checkbox"
                    />
                  </div>
                </th>
                <th className="hmc-th">
                  <div className="hmc-th-content">
                    <svg className="hmc-th-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Date
                  </div>
                </th>
                <th className="hmc-th">
                  <div className="hmc-th-content">
                    <svg className="hmc-th-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Time
                  </div>
                </th>
                <th className="hmc-th hmc-th-event">
                  <div className="hmc-th-content">
                    <svg className="hmc-th-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    Event
                  </div>
                </th>
                <th className="hmc-th">Type</th>
                <th className="hmc-th">Genre</th>
                <th className="hmc-th">
                  <div className="hmc-th-content">
                    <svg className="hmc-th-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    YouTube
                  </div>
                </th>
                <th className="hmc-th">
                  <div className="hmc-th-content">
                    <svg className="hmc-th-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Hero Image
                  </div>
                </th>
                <th className="hmc-th">
                  <div className="hmc-th-content">
                    <svg className="hmc-th-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                    </svg>
                    Tickets
                  </div>
                </th>
                <th className="hmc-th">
                  <div className="hmc-th-content">
                    <svg className="hmc-th-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Status
                  </div>
                </th>
                <th className="hmc-th">
                  <div className="hmc-th-content">
                    <svg className="hmc-th-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Actions
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.map((event, index) => {
                const eventDate = parseLocalDate(event.event_date);
                const isSelected = selectedEventIds.has(event.id);
                const isViewing = isModalOpen && selectedEvent?.id === event.id;
                return (
                  <tr
                    key={event.id}
                    className={`hmc-tr ${isSelected ? 'hmc-tr-selected' : ''} ${isViewing ? 'hmc-tr-viewing' : ''} ${index % 2 === 0 ? 'hmc-tr-even' : 'hmc-tr-odd'}`}
                    onDoubleClick={() => handleViewDetails(event)}
                    title="Double-click to view details"
                  >
                    <td className="hmc-td hmc-td-checkbox">
                      <div className="hmc-checkbox-wrapper">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleEventSelection(event.id)}
                          className="hmc-checkbox"
                        />
                      </div>
                    </td>
                    <td className="hmc-td hmc-td-date">
                      <EditableTableCell
                        value={event.event_date?.split('T')[0] || ''}
                        type="date"
                        onSave={(value) => updateEvent(event.id, { event_date: value || undefined })}
                        displayValue={
                          <div className="hmc-date-cell">
                            <span className="hmc-date-month">
                              {eventDate?.toLocaleDateString('en-US', { month: 'short' }) ?? 'TBD'}
                            </span>
                            <span className="hmc-date-day">
                              {eventDate?.getDate() ?? ''}
                            </span>
                            <span className="hmc-date-year">
                              {eventDate?.getFullYear() ?? ''}
                            </span>
                          </div>
                        }
                        emptyDisplay={
                          <div className="hmc-date-cell">
                            <span className="hmc-date-month text-dark-muted">TBD</span>
                          </div>
                        }
                      />
                    </td>
                    <td className="hmc-td hmc-td-time">
                      <EditableTableCell
                        value={event.event_start_time || event.kickoff_time || null}
                        type="time"
                        placeholder="7:30 PM"
                        onSave={(value) => updateEvent(event.id, { event_start_time: value })}
                        displayValue={
                          event.event_start_time ? (
                            <span className="hmc-time-badge hmc-time-confirmed">
                              {event.event_start_time}
                            </span>
                          ) : (
                            <span className="hmc-time-badge hmc-time-tbd">
                              {event.kickoff_time || 'TBD'}
                            </span>
                          )
                        }
                        emptyDisplay={<span className="hmc-time-badge hmc-time-tbd">TBD</span>}
                      />
                    </td>
                    <td className="hmc-td hmc-td-event">
                      <EditableTableCell
                        value={event.opponent}
                        type="text"
                        placeholder="Event name..."
                        onSave={(value) => updateEvent(event.id, { opponent: value })}
                        displayValue={
                          <div className="hmc-event-name flex items-center gap-2">
                            <span>{getCleanEventName(event.opponent) || 'TBD'}</span>
                            {isAssignedSuiteEvent(event.opponent) && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                Assigned
                              </span>
                            )}
                          </div>
                        }
                        emptyDisplay={<div className="hmc-event-name">TBD</div>}
                      />
                    </td>
                    <td className="hmc-td hmc-td-type">
                      {(() => {
                        const typeResult = detectEventType(event);
                        return (
                          <EditableTableCell
                            value={typeResult.type}
                            type="select"
                            options={EVENT_TYPE_OPTIONS}
                            onSave={(value) => updateEvent(event.id, { event_type: value as EventTypeExtended })}
                            displayValue={
                              <span className={`hmc-type-badge ${typeResult.cssClass}`}>
                                {typeResult.label}
                              </span>
                            }
                          />
                        );
                      })()}
                    </td>
                    <td className="hmc-td hmc-td-genre">
                      <EditableTableCell
                        value={event.music_genre}
                        type="select"
                        options={GENRE_OPTIONS}
                        onSave={(value) => updateEvent(event.id, { music_genre: value as MusicGenre | null })}
                        displayValue={
                          event.music_genre ? (
                            <span className={`hmc-genre-badge ${GENRE_STYLES[event.music_genre]?.bg || 'bg-dark-surface/50/80'}`}>
                              {GENRE_STYLES[event.music_genre]?.label || event.music_genre}
                            </span>
                          ) : null
                        }
                        emptyDisplay={<span className="hmc-genre-empty">-</span>}
                      />
                    </td>
                    <td className="hmc-td hmc-td-youtube">
                      <EditableTableCell
                        value={event.youtube_url}
                        type="text"
                        placeholder="YouTube URL..."
                        onSave={(value) => updateEvent(event.id, { youtube_url: value })}
                        displayValue={
                          event.youtube_url ? (
                            <a
                              href={event.youtube_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hmc-youtube-link"
                              onClick={(e) => e.stopPropagation()}
                              title={event.youtube_url}
                            >
                              <svg className="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                              </svg>
                            </a>
                          ) : null
                        }
                        emptyDisplay={<span className="hmc-youtube-empty">-</span>}
                      />
                    </td>
                    <td className="hmc-td hmc-td-hero-image">
                      <EditableTableCell
                        value={event.event_image_url}
                        type="text"
                        placeholder="Image URL..."
                        onSave={(value) => updateEvent(event.id, { event_image_url: value })}
                        displayValue={
                          event.event_image_url ? (
                            <a
                              href={event.event_image_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hmc-hero-image-link"
                              onClick={(e) => e.stopPropagation()}
                              title={event.event_image_url}
                            >
                              <svg className="w-4 h-4 text-brand-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </a>
                          ) : null
                        }
                        emptyDisplay={<span className="hmc-hero-image-empty">-</span>}
                      />
                    </td>
                    <td className="hmc-td hmc-td-tickets">
                      {(() => {
                        const capacitySplit = getTicketCapacitySplit(
                          event.special_event,
                          event.opponent,
                          event.event_type,
                          event.venue
                        );
                        // In guest mode: show EB remaining only (capped at maxGuestTickets)
                        if (isGuestView) {
                          const displayRemaining = Math.min(capacitySplit.ebRemaining, maxGuestTickets);
                          const statusClass = displayRemaining === 0
                            ? 'hmc-tickets-sold-out'
                            : displayRemaining <= 2
                            ? 'hmc-tickets-low'
                            : 'hmc-tickets-available';
                          return (
                            <div className={`hmc-tickets-cell ${statusClass}`}>
                              <span className="hmc-tickets-text">
                                <span className="hmc-tickets-remaining">
                                  {displayRemaining === 0 ? 'Sold Out' : `${displayRemaining} avail`}
                                </span>
                              </span>
                            </div>
                          );
                        }
                        // Admin mode: show PC/EB split with edit button
                        const hasBookings = capacitySplit.pcUsed > 0 || capacitySplit.ebUsed > 0;
                        if (hasBookings) {
                          const ebStatusClass = capacitySplit.ebRemaining === 0
                            ? 'hmc-tickets-sold-out'
                            : capacitySplit.ebRemaining <= 3
                            ? 'hmc-tickets-low'
                            : 'hmc-tickets-available';
                          return (
                            <div className={`hmc-tickets-cell ${ebStatusClass} group`}>
                              <div className="flex items-center gap-2">
                                <div className="flex flex-col gap-0.5">
                                  <div className="flex items-center gap-1 text-[10px]">
                                    <span className="text-purple-400 font-medium">PC</span>
                                    <span className="text-purple-300">{capacitySplit.pcUsed}/{capacitySplit.pcCapacity}</span>
                                  </div>
                                  <div className="flex items-center gap-1 text-[10px]">
                                    <span className="text-blue-400 font-medium">EB</span>
                                    <span className="text-blue-300">{capacitySplit.ebUsed}/{capacitySplit.ebCapacity}</span>
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenAssignmentModal(event);
                                  }}
                                  className="p-1 rounded hover:bg-dark-border/50 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Edit PC/EB assignments"
                                >
                                  <svg className="w-3.5 h-3.5 text-dark-muted hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className="hmc-tickets-cell hmc-tickets-full group">
                            <div className="flex items-center gap-2">
                              <span className="hmc-tickets-all-available">{capacitySplit.ebCapacity} EB avail</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenAssignmentModal(event);
                                }}
                                className="p-1 rounded hover:bg-dark-border/50 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Edit PC/EB assignments"
                              >
                                <svg className="w-3.5 h-3.5 text-dark-muted hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="hmc-td hmc-td-status">
                      {(() => {
                        const status = event.enrichment_status || 'pending';
                        const statusConfig: Record<string, { label: string; className: string; icon: React.ReactElement }> = {
                          enriched: {
                            label: 'Enriched',
                            className: 'bg-green-500/20 text-green-400 border-green-500/30',
                            icon: (
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ),
                          },
                          pending: {
                            label: 'Pending',
                            className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                            icon: (
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            ),
                          },
                          failed: {
                            label: 'Failed',
                            className: 'bg-red-500/20 text-red-400 border-red-500/30',
                            icon: (
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            ),
                          },
                          skipped: {
                            label: 'Skipped',
                            className: 'bg-gray-500/20 text-dark-muted border-gray-500/30',
                            icon: (
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                              </svg>
                            ),
                          },
                        };
                        const config = statusConfig[status] || statusConfig.pending;
                        return (
                          <span
                            className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full border ${config.className}`}
                            title={event.enriched_at ? `Enriched: ${new Date(event.enriched_at).toLocaleString()}` : undefined}
                          >
                            {config.icon}
                            {config.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="hmc-td hmc-td-actions">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(event);
                        }}
                        className="hmc-view-details-btn"
                        title="View event details"
                        data-testid="view-details-button"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span>View</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty state - hidden in guest view */}
      {!isGuestView && !loading && filteredEvents.length === 0 && events.length > 0 && (
        <div className="text-center py-10">
          <p className="text-dark-muted">No events match your filters.</p>
          <button
            onClick={() => {
              setSearchQuery('');
              setEventTypeFilter('all');
              setAvailabilityFilter('all');
              setEnrichmentFilter('all');
            }}
            className="mt-2 text-brand-400 hover:text-brand-300"
            data-testid="clear-filters-button"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          currentIndex={currentEventIndex}
          totalEvents={filteredEvents.length}
          onPrevious={handlePreviousEvent}
          onNext={handleNextEvent}
          allEvents={events}
          onPanelStateChange={handlePanelStateChange}
          isGuestView={isGuestView}
          maxGuestTickets={maxGuestTickets}
          onEditAssignments={handleOpenAssignmentModal}
        />
      )}

      {/* Ticket Assignment Modal for PC/EB section assignments */}
      {assignmentEvent && (
        <TicketAssignmentModal
          event={assignmentEvent}
          isOpen={isAssignmentModalOpen}
          onClose={handleCloseAssignmentModal}
        />
      )}

      {/* Floating Button for Backup Selection (shows when minimized) - hidden in guest view */}
      {!isGuestView && (
        <BackupSelectionFloatingButton
          allEvents={events}
          onRestore={() => {
            // Find the primary event and open its detail modal
            const primaryEvent = events.find((e) => e.id === backupSelection.primaryEventId);
            if (primaryEvent) {
              setSelectedEvent(primaryEvent);
              setIsModalOpen(true);
              // Restore backup selection (un-minimize)
              restoreBackupSelection();
            }
          }}
        />
      )}

      {/* Re-enrichment Confirmation Modal */}
      {showReEnrichConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="re-enrich-modal">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleCancelReEnrich}
          />

          {/* Modal content */}
          <div className="relative bg-dark-surface border border-dark-border/30 rounded-xl w-full max-w-lg p-6 shadow-2xl">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Re-enrich Already Enriched Events?
                </h2>
                <p className="text-sm text-dark-muted">
                  {alreadyEnrichedEvents.length === 1
                    ? 'This event has already been enriched'
                    : `${alreadyEnrichedEvents.length} events have already been enriched`}
                </p>
              </div>
            </div>

            {/* List of already enriched events */}
            <div className="max-h-60 overflow-y-auto mb-6 space-y-2">
              {alreadyEnrichedEvents.map((event) => {
                const eventDate = parseLocalDate(event.event_date);
                const formattedDate = eventDate?.toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                }) ?? 'Date TBD';
                return (
                  <div
                    key={event.id}
                    className="p-3 bg-dark-border/50 rounded-xl border border-dark-border/30"
                  >
                    <p className="text-white font-medium text-sm">
                      {event.opponent || event.special_event || 'Unknown Event'}
                    </p>
                    <p className="text-dark-muted text-xs mt-0.5">
                      {formattedDate}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Confirmation message */}
            <p className="text-sm text-dark-text mb-6">
              Are you sure you want to re-enrich {alreadyEnrichedEvents.length === 1
                ? 'this already enriched event'
                : `these ${alreadyEnrichedEvents.length} already enriched events`}?
              This will overwrite existing enrichment data.
            </p>

            {/* Action buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelReEnrich}
                className="px-4 py-2 bg-dark-border text-white rounded-lg hover:bg-dark-border/70 transition-all duration-150 min-h-[44px]"
                data-testid="re-enrich-cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReEnrich}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-all duration-150 min-h-[44px]"
                data-testid="re-enrich-confirm-button"
              >
                Re-enrich {alreadyEnrichedEvents.length === 1 ? 'Event' : `${alreadyEnrichedEvents.length} Events`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventReportViewer;
